import clsx from "clsx";
import {
  Form,
  Formik,
  useFormikContext,
  type FormikConfig,
  type FormikErrors,
  type FormikTouched,
  type FormikValues,
} from "formik";
import { produce } from "immer";
import { omit } from "lodash";
import { createContext, useCallback, useContext, useEffect } from "react";
import { useImmer, type DraftFunction } from "use-immer";

export interface FormProviderProps<TValues extends FormikValues>
  extends FormikConfig<TValues> {
  className?: string;
}
export type FormConfigProps<TValues extends FormikValues> = Omit<
  FormProviderProps<TValues>,
  "children"
>;

const FormConfigContext = createContext<
  // Since this is an internal context declaration,
  // I think we can get away with using `any` to avoid the hassle of overly complicated types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- types
  ((updateFn: DraftFunction<Partial<FormConfigProps<any>>>) => void) | null
>(null);
/**
 *
 * Requires:
 * - `initialValues`: an object with the initial state for the fields in the form.
 * - `onSubmit`: a function that is called with the current values in the form upon a *successful* submit.
 *
 * Optional:
 * - `validationSchema`: a [yup](https://github.com/jquense/yup) schema;
 * will be checked against the values when the user submits.
 * - `className`: additional className for `Form-body`, the containing element to be styled
 *
 * For other props view [the formik docs](https://formik.org/docs/api/formik)
 */
function FormProvider<TValues extends FormikValues>(
  props: FormProviderProps<TValues>,
) {
  const { children, ...rest } = props;
  const [config, updateConfig] = useImmer<Partial<FormConfigProps<TValues>>>(
    {},
  );

  const useFormConfig = useCallback(
    (updateFn: DraftFunction<Partial<FormConfigProps<TValues>>>) => {
      updateConfig(updateFn);
    },
    [],
  );

  return (
    <Formik
      {...omit(rest, "dir", "className", "translateFn")}
      {...omit(config, "dir", "className", "translateFn")}
    >
      {(params) => (
        <Form
          className={clsx("Form-actual-form", rest.className, config.className)}
        >
          <FormConfigContext.Provider value={useFormConfig}>
            {typeof children === "function" ? children(params) : children}
          </FormConfigContext.Provider>
        </Form>
      )}
    </Formik>
  );
}

export function useForm<TValues>() {
  const { setValues, errors, setErrors, touched, setTouched, ...rest } =
    useFormikContext<TValues>();

  const setValuesWithImmer = (
    updater: TValues | DraftFunction<TValues>,
    shouldValidate?: boolean,
  ) =>
    setValues(
      (prev) =>
        typeof updater === "function"
          ? produce(prev, updater as DraftFunction<TValues>)
          : updater,
      shouldValidate,
    );

  const setErrorsWithImmer = (
    updater: FormikErrors<TValues> | DraftFunction<FormikErrors<TValues>>,
  ) =>
    setErrors(
      typeof updater === "function" ? produce(errors, updater) : updater,
    );

  const setTouchedWithImmer = (
    updater: FormikTouched<TValues> | DraftFunction<FormikTouched<TValues>>,
    shouldValidate?: boolean,
  ) =>
    setTouched(
      typeof updater === "function" ? produce(touched, updater) : updater,
      shouldValidate,
    );

  return {
    ...rest,
    errors,
    setErrors: setErrorsWithImmer,
    setTouched: setTouchedWithImmer,
    setValues: setValuesWithImmer,
    touched,
  };
}
export type FormDetails<TValues> = ReturnType<typeof useForm<TValues>>;
export const useFormConfig = <TValues extends FormikValues>(
  updateFn: DraftFunction<Partial<FormConfigProps<TValues>>>,
  dependencies?: React.DependencyList,
) => {
  const updateConfig = useContext(FormConfigContext);
  useEffect(() => {
    if (updateConfig) updateConfig(updateFn);
  }, dependencies || []);
};
export default FormProvider;
